import React, { Component, createRef } from "react"
import { concat, of, Subject, Subscription } from "rxjs"
import { catchError, debounceTime, distinctUntilChanged, filter, map, switchMap } from "rxjs/operators"
import { AssignedClinic, WorkforceClinicPortal } from "../../AppState"
import { AsyncTypeahead } from "react-bootstrap-typeahead"
import CompaniesGetObservable, { CompaniesFilterParams } from "../../api/Portals/PortalCompaniesGet"
import { AccountLimit } from "../../config/Constants"
import { Form, Row } from "react-bootstrap"
import UserAccessSettings from "../../authentication/UserAccessSettings"
import UserState from "../../authentication/UserState"

interface PortalCompanySearchProps {
  id?: string
  portal: WorkforceClinicPortal
  switchCompany(company: AssignedClinic): void
  showCompaniesSearch: boolean
  userAccess: UserAccessSettings
  userState: UserState
}

interface PortalCompanySearchState {
  loading: boolean
  companies?: AssignedClinic[]
}

export class PortalCompanySearch extends Component<PortalCompanySearchProps, PortalCompanySearchState> {
  state: PortalCompanySearchState = { loading: false, companies: [] }
  companies$?: Subject<CompaniesFilterParams>;
  companiesSubscription?: Subscription;
  companyRef = createRef<AsyncTypeahead<AssignedClinic>>()

  clearResult() {
    this.companyRef?.current?.clear();
    this.setState({ companies: [] });
  }

  searchCompanies(query: string, locationsOnly: boolean = false, accountID: number = 0) {
    this.companies$?.next({ portal: this.props.portal, query: query, locationsOnly: locationsOnly, accountID: accountID })
  }

  componentDidMount() {
    this.companies$ = new Subject<CompaniesFilterParams>();
    this.companiesSubscription = this.companies$.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      filter(params => (params.query ?? "").trim().length > 0),
      switchMap((params) => concat(
        of({ loading: true }),
        CompaniesGetObservable(params).pipe(
          map((companies) => ({ loading: false, companies: companies })),
          catchError(() => of({ loading: false }))
        )
      ))
    ).subscribe(
      (o: { loading: boolean, companies?: AssignedClinic[] }) => {
        this.setState({ loading: o.loading, companies: o.companies })
      },
      () => this.setState({ loading: false, companies: [] })
    )
  }

  componentWillUnmount() {
    this.companiesSubscription?.unsubscribe()
  }

  render() {
    const { loading, companies } = this.state
    const { id, switchCompany, userAccess, userState, portal, showCompaniesSearch } = this.props
    const accountID = userAccess.isCompanyAdmin || userAccess.isLocationAdmin ? userState.selectedCompany?.AccountID : 0
    const showLocations = (userAccess.isCompanyAdmin && portal.AssignedClientDetailsOfClinic.some(company => company.AccountID === accountID && !!company.LocationName)) ||
                          (userAccess.isLocationAdmin && portal.AssignedClientDetailsOfClinic.some(company => company.AccountID === accountID && !!company.LocationName && company.LocationID !== userState.selectedCompany?.LocationID))

    return (
      <Form>
          {!userAccess.isLocationAdmin && showCompaniesSearch && <Row className="mb-2">
            <AsyncTypeahead
              id={id}
              emptyLabel="No Companies Found"
              placeholder="Enter the Company name"
              labelKey="Name"
              minLength={0}
              maxResults={AccountLimit}
              paginate={false}
              useCache={false}
              isLoading={loading}
              inputProps={{ style: { 'width': '250px', 'margin': '0 15px' }, autoComplete: "chrome-off" }}
              onBlur={() => this.clearResult()}
              onSearch={query => this.searchCompanies(query)}
              onChange={(selected: AssignedClinic[]) => switchCompany(selected[0])}
              options={companies || []}
              positionFixed
              ref={this.companyRef}
              selected={[]}>
          </AsyncTypeahead>
        </Row>}
        {showLocations && <Row>
          <AsyncTypeahead
            id={id}
            emptyLabel="No Locations Found"
            placeholder="Enter the Location name"
            labelKey={(option) => `${option.LocationName}`}
            minLength={0}
            maxResults={AccountLimit}
            paginate={false}
            useCache={false}
            isLoading={loading}
            inputProps={{ style: { 'width': '250px', 'margin': '0 15px' }, autoComplete: "chrome-off" }}
            onBlur={() => this.clearResult()}
            onSearch={query => this.searchCompanies(query, true, accountID)}
            onChange={(selected: AssignedClinic[]) => switchCompany(selected[0])}
            options={companies || []}
            positionFixed
            ref={this.companyRef}
            selected={[]}>
          </AsyncTypeahead>
        </Row>}
      </Form>
    )
  }
}
